import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

type Props = {
  images: string[];
  interval?: number;
};

export function Carousel({ images, interval = 3500 }: Props) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState<Record<number, boolean>>({});
  const startX = useRef<number | null>(null);

  const goTo = useCallback(
    (next: number, dir: number) => {
      setDirection(dir);
      setIndex((next + images.length) % images.length);
    },
    [images.length]
  );

  useEffect(() => {
    if (paused || images.length <= 1) return;
    const id = setInterval(() => goTo(index + 1, 1), interval);
    return () => clearInterval(id);
  }, [index, paused, images.length, interval, goTo]);

  // 현재 + 다음 이미지 프리로드
  useEffect(() => {
    const toLoad = [index, (index + 1) % images.length];
    toLoad.forEach((i) => {
      if (loaded[i]) return;
      const img = new Image();
      img.src = images[i];
      img.onload = () => setLoaded((prev) => ({ ...prev, [i]: true }));
    });
  }, [index, images]); // eslint-disable-line react-hooks/exhaustive-deps

  function onPointerDown(e: React.PointerEvent) {
    startX.current = e.clientX;
  }

  function onPointerUp(e: React.PointerEvent) {
    if (startX.current === null) return;
    const diff = e.clientX - startX.current;
    if (Math.abs(diff) > 40) {
      goTo(diff < 0 ? index + 1 : index - 1, diff < 0 ? 1 : -1);
    }
    startX.current = null;
  }

  const variants = {
    enter: (d: number) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0 }),
  };

  return (
    <div
      className="relative w-full overflow-hidden bg-gray-100"
      style={{ aspectRatio: "16/9" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      {/* Skeleton — 현재 이미지가 아직 로드되지 않았을 때 */}
      {!loaded[index] && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100">
          <motion.div
            className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-brand-400"
            animate={{ rotate: 360 }}
            transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
          />
        </div>
      )}

      <AnimatePresence initial={false} custom={direction} mode="popLayout">
        <motion.img
          key={index}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.45, ease: "easeInOut" }}
          src={images[index]}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
          onLoad={() => setLoaded((prev) => ({ ...prev, [index]: true }))}
        />
      </AnimatePresence>

      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i, i > index ? 1 : -1)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-4 bg-white" : "w-1.5 bg-white/50"
              }`}
              aria-label={`이미지 ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
